import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Play, Save, Filter, Search, Music4, ListMusic, Check, ChevronDown, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getLeagueNameFromSlug, isValidSlug } from '@/lib/league-slugs';
import Navigation from '@/components/Navigation';
import { useStore } from '@/lib/store';
import { usePlayback } from '@/lib/playback';
import { usePersonalPlaylists } from '@/hooks/use-personal-playlists';
import { useArchivePlaylists } from '@/hooks/useArchivePlaylists';
import { useToast } from '@/hooks/use-toast';
import { type PlaylistFilters } from '@/lib/playlist-utils';
import { extractYouTubeId } from '@/lib/utils';
import type { PlaylistTrack, PlaylistReaction } from '@/lib/playback';
import { mapArchiveEntryToTrack, type ArchiveEntry } from '@/lib/archive';
import { supabase } from '@/lib/supabase';
import * as api from '@/lib/api';
import { PlaylistLinks } from '@/components/PlaylistLinks';
import type { CuratedPlaylist } from '@/lib/mock-data';
import { getSmartlinkUrl } from '@/lib/smartlinks';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

type DataSource = 'live' | 'archive';

interface FilteredTrack {
  submissionId: string;
  roundId: string;
  title: string;
  artist: string;
  submitter: string;
  score: number;
  rank: number;
  totalInRound: number;
  season: string;
  roundName: string;
  youtubeId?: string;
  thumbnail?: string;
  comment?: string;
}

function getDenseRankForRound(submissions: Array<{ id: string; total_points: number }>, submissionId: string): number {
  // Dense rank: 31,25,25,21 -> 1,2,2,3
  const sorted = submissions.slice().sort((a, b) => b.total_points - a.total_points);
  let rank = 0;
  let lastScore: number | null = null;
  for (const s of sorted) {
    if (lastScore === null || s.total_points !== lastScore) {
      rank += 1;
      lastScore = s.total_points;
    }
    if (s.id === submissionId) return rank;
  }
  return 0;
}

interface PlaylistsProps {
  leagueSlug?: string;
  leagueName?: string;
  displayName?: string;
}

const Playlists = ({ leagueSlug: propLeagueSlug, leagueName: propLeagueName, displayName }: PlaylistsProps = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useStore();
  
  // Get league slug from URL if not provided via props
  const urlLeagueSlug = useMemo(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0 && isValidSlug(pathParts[0])) {
      return pathParts[0];
    }
    return null;
  }, [location.pathname]);
  
  // Use prop league slug if provided, otherwise use URL slug
  const leagueSlug = propLeagueSlug || urlLeagueSlug || null;

  const toKebabSlug = (value: string) =>
    value
      .replace(/([a-z])([A-Z])/g, "$1-$2")
      .replace(/[_\s]+/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();

  const derivedLeagueName = useMemo(() => {
    if (!leagueSlug) return null;
    const mapped = getLeagueNameFromSlug(leagueSlug);
    if (mapped) return mapped;
    return leagueSlug
      .replace(/[_-]+/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .trim();
  }, [leagueSlug]);

  const leagueName = propLeagueName || derivedLeagueName;
  const [curatedPlaylists, setCuratedPlaylists] = useState<CuratedPlaylist[]>([]);
  const { rounds, submissions: storeSubmissions, users, currentUser } = state;
  const playback = usePlayback();
  const { playlists, createPlaylist } = usePersonalPlaylists();
  const { data: archiveData, isLoading: archiveLoading } = useArchivePlaylists();
  const { toast } = useToast();
  
  // Fetch ALL submissions and rounds from user's leagues (not just loaded ones)
  // OR from a specific league if leagueName is provided
  const [allSubmissions, setAllSubmissions] = useState<typeof storeSubmissions>([]);
  const [allRounds, setAllRounds] = useState<typeof rounds>([]);
  const [allSeasonsFromDB, setAllSeasonsFromDB] = useState<any[]>([]);
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoadingSubmissions(true);
      try {
        let leagueIds: string[] = [];
        
        if (leagueSlug) {
          // Prefer slug lookup to support public playlist views without login
          const slugCandidates = Array.from(
            new Set([leagueSlug, toKebabSlug(leagueSlug)]),
          );
          const orClause = slugCandidates.map((slug) => `slug.ilike.${slug}`).join(',');
          const { data: leagueBySlug, error: leagueBySlugError } = await supabase
            .from('leagues')
            .select('id, name')
            .or(orClause)
            .maybeSingle();
          if (leagueBySlugError) {
            console.warn('[Playlists] Failed to fetch league by slug:', leagueBySlugError);
          }
          if (leagueBySlug) {
            leagueIds = [leagueBySlug.id];
          } else if (leagueName) {
            // Fallback to name lookup if slug doesn't resolve
            const { data: leagueByName, error: leagueByNameError } = await supabase
              .from('leagues')
              .select('id, name')
              .ilike('name', leagueName)
              .maybeSingle();
            if (leagueByNameError) {
              console.warn('[Playlists] Failed to fetch league by name:', leagueByNameError);
            }
            if (leagueByName) {
              leagueIds = [leagueByName.id];
            }
          }
          if (leagueIds.length === 0) {
            console.warn(`[Playlists] League "${leagueSlug}" not found`);
            setAllSubmissions([]);
            setAllRounds([]);
            setIsLoadingSubmissions(false);
            return;
          }
        } else if (leagueName) {
          // Filter by specific league name (case-insensitive)
          const allLeagues = await api.leagueAPI.getAllLeagues();
          const matchingLeague = allLeagues.find(l =>
            l.name.toLowerCase() === leagueName.toLowerCase()
          );
          if (matchingLeague) {
            leagueIds = [matchingLeague.id];
          } else {
            console.warn(`[Playlists] League "${leagueName}" not found`);
            setAllSubmissions([]);
            setAllRounds([]);
            setIsLoadingSubmissions(false);
            return;
          }
        } else if (currentUser?.id) {
          // Get user's leagues (legacy behavior)
          const userLeagues = await api.leagueAPI.getLeaguesByUser(currentUser.id);
          leagueIds = userLeagues.map(l => l.id);
        } else {
          // No user and no league specified - show nothing or all leagues?
          // For now, show nothing
          setAllSubmissions([]);
          setAllRounds([]);
          setIsLoadingSubmissions(false);
          return;
        }
        
        // Fetch curated playlists filtered by leagues
        const curated = await api.curatedPlaylistAPI.getCuratedPlaylists(leagueIds);
        console.log('[Playlists] Fetched curated playlists:', curated.length, 'for leagueIds:', leagueIds, curated.map(p => ({ name: p.name, season_id: (p as any).season_id, filters: p.filters })));
        setCuratedPlaylists(curated);
        
        // Fetch current playlist ID from league if league is specified
        if (leagueName && leagueIds.length > 0) {
          const { data: leagueData } = await supabase
            .from('leagues')
            .select('current_playlist_id')
            .eq('id', leagueIds[0])
            .single();
          
          if (leagueData?.current_playlist_id) {
            // Check if the current playlist exists in curated playlists
            const currentPlaylist = curated.find(p => p.id === leagueData.current_playlist_id);
            if (currentPlaylist) {
              setSelectedPlaylistId(leagueData.current_playlist_id);
              setSelectedPlaylistType('curated');
            }
          }
        }
        
        // Fetch all seasons for leagues
        // ALWAYS filter by leagueIds when leagueName is provided, regardless of user
        if (leagueName && leagueIds.length > 0) {
          // Filter by specific league
          const { data: seasonsData } = await supabase
            .from('seasons')
            .select('*')
            .in('league_id', leagueIds);
          setAllSeasonsFromDB(seasonsData || []);
        } else if (currentUser?.id) {
          // Legacy behavior: get all seasons for user (when no leagueName specified)
          const seasons = await api.seasonAPI.getAllSeasonsForUser(currentUser.id);
          setAllSeasonsFromDB(seasons);
        } else {
          // No user and no league specified
          setAllSeasonsFromDB([]);
        }
        
        if (leagueIds.length === 0) {
          setAllSubmissions([]);
          setAllRounds([]);
          setIsLoadingSubmissions(false);
          return;
        }
        
        // Get all rounds from user's leagues (with full data including season)
        const { data: allRoundsData, error: roundsError } = await supabase
          .from('rounds')
          // New schema: season name is on seasons.name; rounds may not have a `season` column.
          .select(`
            id,
            league_id,
            round_number,
            theme,
            description,
            season_id,
            submission_deadline,
            voting_deadline,
            status,
            created_at,
            seasons:seasons!rounds_season_id_fkey(name)
          `)
          .in('league_id', leagueIds);
        
        if (roundsError || !allRoundsData || allRoundsData.length === 0) {
          console.warn('[Playlists] Could not fetch rounds for user leagues');
          setAllSubmissions(storeSubmissions);
          setAllRounds(rounds);
          setIsLoadingSubmissions(false);
          return;
        }
        
        // Convert to Round format
        const convertedRounds = allRoundsData.map((dbRound: any) => ({
          id: dbRound.id,
          league_id: dbRound.league_id,
          round_number: dbRound.round_number,
          theme: dbRound.theme,
          description: dbRound.description || undefined,
          season: dbRound.seasons?.name || dbRound.season || undefined,
          submission_deadline: dbRound.submission_deadline,
          voting_deadline: dbRound.voting_deadline,
          status: dbRound.status,
          created_at: dbRound.created_at,
        }));
        
        setAllRounds(convertedRounds);
        const roundIds = allRoundsData.map(r => r.id);
        
        // Fetch all submissions from these rounds
        const { data: allSubs, error: subsError } = await supabase
          .from('submissions')
          .select(`
            *,
            users:user_id (
              username,
              avatar_url
            )
          `)
          .in('round_id', roundIds);
        
        if (subsError) {
          console.error('[Playlists] Error fetching all submissions:', subsError);
          setAllSubmissions(storeSubmissions);
        } else {
          // Convert to Submission format
          const convertedSubs = (allSubs || []).map((dbSub: any) => {
            const sub = {
              id: dbSub.id,
              round_id: dbSub.round_id,
              user_id: dbSub.user_id,
              song_title: dbSub.song_title,
              artist_name: dbSub.artist_name,
              submitter_comment: dbSub.submitter_comment || undefined,
              video_url: dbSub.youtube_url || undefined,
              total_points: dbSub.total_points || 0,
              submitted_at: dbSub.submitted_at,
              spotify_track_id: dbSub.spotify_track_id || undefined,
              spotify_url: dbSub.spotify_url || undefined,
              youtube_video_id: dbSub.youtube_video_id || undefined,
              fun_facts: dbSub.fun_facts || undefined,
              username: dbSub.users?.username || undefined,
            };
            return sub;
          });

          // If usernames didn't come through (RLS can block joins), backfill from users table.
          try {
            const missingUserIds = Array.from(
              new Set(
                convertedSubs
                  .filter((s) => s.user_id && !s.username)
                  .map((s) => s.user_id)
              )
            );

            if (missingUserIds.length > 0) {
              const usersMap = new Map<string, string>();
              const batchSize = 1000;

              for (let i = 0; i < missingUserIds.length; i += batchSize) {
                const batch = missingUserIds.slice(i, i + batchSize);
                const { data: usersData, error: usersError } = await supabase
                  .from('users')
                  .select('id, username')
                  .in('id', batch);

                if (usersError) {
                  console.warn('[Playlists] Could not backfill usernames from users table:', usersError);
                  break;
                }

                (usersData || []).forEach((u: any) => {
                  if (u?.id && u?.username) usersMap.set(u.id, u.username);
                });
              }

              if (usersMap.size > 0) {
                convertedSubs.forEach((s: any) => {
                  if (!s.username && s.user_id && usersMap.has(s.user_id)) {
                    s.username = usersMap.get(s.user_id);
                  }
                });
              }
            }
          } catch (e) {
            console.warn('[Playlists] Username backfill failed (non-fatal):', e);
          }
          
          console.log(`[Playlists] ✅ Loaded ${convertedSubs.length} submissions and ${convertedRounds.length} rounds from ${leagueIds.length} leagues`);
          setAllSubmissions(convertedSubs);
        }
      } catch (error) {
        console.error('[Playlists] Error fetching all data:', error);
        setAllSubmissions(storeSubmissions);
        setAllRounds(rounds);
      } finally {
        setIsLoadingSubmissions(false);
      }
    };
    
    fetchAllData();
  }, [currentUser?.id, leagueName, storeSubmissions, rounds]);

  // Log when archive data loads
  useEffect(() => {
    if (archiveData) {
      console.log('[Playlists] 📊 Archive data loaded:', {
        totalEntries: archiveData.entries?.length || 0,
        seasons: archiveData.grouped?.length || 0,
        isLoading: archiveLoading,
      });
      
      // Count entries by season
      if (archiveData.grouped) {
        archiveData.grouped.forEach(group => {
          const totalInSeason = Object.values(group.rounds).flat().length;
          const withYouTube = Object.values(group.rounds)
            .flat()
            .filter((e: ArchiveEntry) => {
              const youtubeId = extractYouTubeId(e.youtube_url || '');
              return !!youtubeId;
            }).length;
          console.log(`[Playlists] 📋 ${group.season}:`, {
            total: totalInSeason,
            withYouTube,
            withoutYouTube: totalInSeason - withYouTube,
          });
        });
      }
    }
  }, [archiveData, archiveLoading]);

  // Data source selection
  const [dataSource, setDataSource] = useState<DataSource>('archive');
  
  // Playlist selection
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string>('none');
  const [selectedPlaylistType, setSelectedPlaylistType] = useState<'curated' | 'personal' | 'none'>('none');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);
  const [selectedSubmitters, setSelectedSubmitters] = useState<string[]>([]);
  const [selectedRanks, setSelectedRanks] = useState<number[]>([]);
  const [selectedRoundIds, setSelectedRoundIds] = useState<string[]>([]);

  // Playlist creation
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistDescription, setPlaylistDescription] = useState('');

  // Collapse Results by default.
  const [mobileResultsOpen, setMobileResultsOpen] = useState<string>('');

  // Get all available seasons (from database, live rounds, and archive)
  // ONLY include seasons from the current league if leagueName is specified
  const allSeasons = useMemo(() => {
    const seasons = new Map<string, { name: string; sort_index?: number; created_at?: string }>();
    
    // If filtering by league, only use data from that league
    if (leagueName) {
      // From database seasons (has sort_index and created_at) - already filtered by league_id
      allSeasonsFromDB.forEach((season) => {
        seasons.set(season.name, {
          name: season.name,
          sort_index: season.sort_index,
          created_at: season.created_at,
        });
      });
      
      // From live rounds (fallback for seasons not in DB) - use allRounds which is filtered by league
      allRounds.forEach((round) => {
        const seasonName = round.season || 'Season 1';
        if (!seasons.has(seasonName)) {
          seasons.set(seasonName, {
            name: seasonName,
            sort_index: undefined,
            created_at: round.created_at,
          });
        }
      });
      
      // From archive data (fallback) - filter by league if specified
      // Build set of league round IDs for filtering
      const leagueRoundIds = allRounds.length > 0 
        ? new Set(allRounds.map(r => r.id))
        : null;
      
      // Only process archive seasons if we have league round IDs to filter by
      // This prevents BLIS seasons from appearing when viewing Masters of Music
      if (archiveData?.grouped && leagueRoundIds && leagueRoundIds.size > 0) {
        archiveData.grouped.forEach((group) => {
          // Only include seasons that have rounds in this league
          // Check ALL rounds in this season group to see if ANY belong to the current league
          const hasLeagueRounds = Object.values(group.rounds).some((roundEntries: ArchiveEntry[]) =>
            Array.isArray(roundEntries) && roundEntries.some((entry: ArchiveEntry) => 
              entry.round_id && leagueRoundIds.has(entry.round_id)
            )
          );
          
          if (hasLeagueRounds && !seasons.has(group.season)) {
            console.log('[Playlists] ✅ Including archive season in allSeasons:', group.season, 'has rounds in league. leagueRoundIds size:', leagueRoundIds.size);
            seasons.set(group.season, {
              name: group.season,
              sort_index: undefined,
              created_at: undefined,
            });
          } else if (!hasLeagueRounds && group.season.includes('B.L.I.S')) {
            console.log('[Playlists] ❌ Excluding BLIS archive season from allSeasons:', group.season, 'no rounds in league. leagueRoundIds size:', leagueRoundIds.size);
          }
        });
      } else if (archiveData?.grouped && leagueName) {
        // If we have leagueName but no rounds loaded yet, skip archive seasons to prevent BLIS from showing
        console.log('[Playlists] ⚠️ Skipping archive seasons - leagueName present but allRounds not loaded yet. allRounds.length:', allRounds.length);
      }
    } else {
      // No league filter - show all seasons (legacy behavior for logged-in users)
      allSeasonsFromDB.forEach((season) => {
        seasons.set(season.name, {
          name: season.name,
          sort_index: season.sort_index,
          created_at: season.created_at,
        });
      });
      
      allRounds.forEach((round) => {
        const seasonName = round.season || 'Season 1';
        if (!seasons.has(seasonName)) {
          seasons.set(seasonName, {
            name: seasonName,
            sort_index: undefined,
            created_at: round.created_at,
          });
        }
      });
      
      if (archiveData?.grouped) {
        archiveData.grouped.forEach((group) => {
          if (!seasons.has(group.season)) {
            seasons.set(group.season, {
              name: group.season,
              sort_index: undefined,
              created_at: undefined,
            });
          }
        });
      }
    }
    
    // Convert to array and sort by sort_index (newest first), then by created_at, then alphabetically
    return Array.from(seasons.values()).sort((a, b) => {
      // If both have sort_index, use that (higher = newer)
      if (a.sort_index !== undefined && b.sort_index !== undefined) {
        return b.sort_index - a.sort_index;
      }
      
      // If only one has sort_index, prioritize it
      if (a.sort_index !== undefined) return -1;
      if (b.sort_index !== undefined) return 1;
      
      // If both have created_at, use that
      if (a.created_at && b.created_at) {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      
      // Fallback to alphabetical (newer names first)
      return b.name.localeCompare(a.name);
    }).map(s => s.name);
  }, [allSeasonsFromDB, allRounds, archiveData, leagueName]);

  // Get all available submitters (from both live and archive)
  // ONLY include submitters from the current league if leagueName is specified
  const allSubmitters = useMemo(() => {
    const submitters = new Map<string, string>();
    
    // From live data - allSubmissions is already filtered by league
    allSubmissions.forEach((submission) => {
      const user = users.find((u) => u.id === submission.user_id);
      const name = user?.username || submission.username || 'Unknown';
      submitters.set(submission.user_id, name);
    });
    
    // From archive data - filter by league if specified
    if (archiveData?.entries) {
      const leagueRoundIds = leagueName && allRounds.length > 0 
        ? new Set(allRounds.map(r => r.id))
        : null;
      
      if (leagueRoundIds && leagueRoundIds.size > 0) {
        // Only include submitters from this league's rounds
        archiveData.entries.forEach((entry) => {
          if (entry.round_id && leagueRoundIds.has(entry.round_id)) {
            submitters.set(entry.submitter_id, entry.submitter);
          }
        });
      } else if (!leagueName) {
        // No league filter - include all archive submitters
        archiveData.entries.forEach((entry) => {
          submitters.set(entry.submitter_id, entry.submitter);
        });
      }
    }
    
    return Array.from(submitters.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allSubmissions, users, archiveData, allRounds, leagueName]);

  // Get filtered tracks based on current filters
  const filteredTracks = useMemo((): FilteredTrack[] => {
    console.log('[Playlists] 🔄 Filtering tracks...', {
      dataSource,
      selectedSeasons,
      selectedSubmitters: selectedSubmitters.length,
      selectedRanks,
      selectedRoundIds,
      searchQuery,
      archiveEntriesCount: archiveData?.entries?.length || 0,
      liveSubmissionsCount: allSubmissions.length,
    });

    if (dataSource === 'live') {
      // Filter live submissions
      let filtered = allSubmissions
        .filter((submission) => {
          const round = rounds.find((r) => r.id === submission.round_id);
          if (!round) return false;
          
          const season = round.season || 'Season 1';
          if (selectedSeasons.length > 0 && !selectedSeasons.includes(season)) {
            return false;
          }
          
          if (selectedSubmitters.length > 0 && !selectedSubmitters.includes(submission.user_id)) {
            return false;
          }

          if (selectedRoundIds.length > 0 && !selectedRoundIds.includes(submission.round_id)) {
            return false;
          }
          
          return true;
        })
        .map((submission) => {
          const round = allRounds.find((r) => r.id === submission.round_id)!;
          const user = users.find((u) => u.id === submission.user_id);
          const submitterName = user?.username || submission.username || 'Unknown';
          
          // Calculate rank within round
          const roundSubmissions = allSubmissions
            .filter((s) => s.round_id === submission.round_id)
            .sort((a, b) => b.total_points - a.total_points);
          const rank = getDenseRankForRound(
            roundSubmissions.map((s) => ({ id: s.id, total_points: s.total_points })),
            submission.id
          );
          
          return {
            submissionId: submission.id,
            roundId: submission.round_id,
            title: submission.song_title,
            artist: submission.artist_name,
            submitter: submitterName,
            score: submission.total_points,
            rank,
            totalInRound: roundSubmissions.length,
            season: round.season || 'Season 1',
            roundName: round.theme,
            youtubeId: submission.youtube_video_id || extractYouTubeId(submission.video_url || ''),
            comment: submission.submitter_comment,
          };
        });
      
      // Filter by rank
      if (selectedRanks.length > 0) {
        filtered = filtered.filter((track) => selectedRanks.includes(track.rank));
      }
      if (selectedRoundIds.length > 0) {
        filtered = filtered.filter((track) => selectedRoundIds.includes(track.roundId));
      }
      
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (track) =>
            track.title.toLowerCase().includes(query) ||
            track.artist.toLowerCase().includes(query) ||
            track.submitter.toLowerCase().includes(query) ||
            track.season.toLowerCase().includes(query) ||
            track.roundName.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    } else {
      // Filter archive entries only (all songs are archived/imported, no live leagues)
      let entriesToFilter = archiveData?.entries || [];
      
      // Filter archive entries by league if league is specified
      if (leagueName && allRounds.length > 0) {
        const leagueRoundIds = new Set(allRounds.map(r => r.id));
        const beforeFilter = entriesToFilter.length;
        entriesToFilter = entriesToFilter.filter(entry => leagueRoundIds.has(entry.round_id));
        console.log(`[Playlists] 🔍 Filtered archive entries by league: ${beforeFilter} → ${entriesToFilter.length}`);
      }
      
      if (entriesToFilter.length === 0) {
        console.log('[Playlists] ⚠️ No archive entries available');
        return [];
      }
      
      console.log('[Playlists] 📊 Starting with', entriesToFilter.length, 'entries (archive + live)');
      
      // Debug: Check what season names exist in entries
      if (selectedSeasons.length > 0) {
        const uniqueSeasonsInEntries = new Set(entriesToFilter.map(e => e.season));
        console.log('[Playlists] 🔍 Debug season matching:', {
          selectedSeasons,
          uniqueSeasonsInEntries: Array.from(uniqueSeasonsInEntries),
          matches: selectedSeasons.filter(s => uniqueSeasonsInEntries.has(s)),
        });
      }
      
      let filtered = entriesToFilter
        .map((entry, originalIndex) => ({ entry, originalIndex }))
        .filter(({ entry }) => {
          if (selectedSeasons.length > 0 && !selectedSeasons.includes(entry.season)) {
            return false;
          }
          
          if (selectedSubmitters.length > 0 && !selectedSubmitters.includes(entry.submitter_id)) {
            return false;
          }
          
          const rank = entry.rank ? parseInt(entry.rank) : undefined;
          if (selectedRanks.length > 0 && rank && !selectedRanks.includes(rank)) {
            return false;
          }

          if (selectedRoundIds.length > 0 && !selectedRoundIds.includes(entry.round_id)) {
            return false;
          }
          
          return true;
        })
        .map(({ entry, originalIndex }) => {
          // Calculate total submissions in this round from combined entries
          const roundEntries = entriesToFilter.filter((e) => e.round_id === entry.round_id);
          // Use actual submission ID if available, otherwise create unique ID for backward compatibility
          const submissionId = entry.submission_id || `${entry.round_id}-${entry.submitter_id}-${entry.song_title}-${originalIndex}`;
          return {
            submissionId,
            roundId: entry.round_id,
            title: entry.song_title,
            artist: entry.artist,
            submitter: entry.submitter,
            score: entry.score,
            rank: entry.rank ? parseInt(entry.rank) : 0,
            totalInRound: roundEntries.length,
            season: entry.season,
            roundName: entry.round_name,
            youtubeId: extractYouTubeId(entry.youtube_url),
            archiveEntry: entry, // Store the full entry for parsing comments
          };
        });
      
      console.log('[Playlists] ✅ After season/submitter/rank filters:', filtered.length, 'tracks');
      
      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const beforeSearch = filtered.length;
        filtered = filtered.filter(
          (track) =>
            (track.title?.toLowerCase() || '').includes(query) ||
            (track.artist?.toLowerCase() || '').includes(query) ||
            (track.submitter?.toLowerCase() || '').includes(query) ||
            (track.season?.toLowerCase() || '').includes(query) ||
            (track.roundName?.toLowerCase() || '').includes(query)
        );
        console.log('[Playlists] 🔍 After search filter:', filtered.length, 'tracks (was', beforeSearch, ')');
      }
      
      // Count tracks with YouTube IDs
      const withYouTube = filtered.filter(t => {
        const youtubeId = extractYouTubeId((t as any).archiveEntry?.youtube_url || '');
        return !!youtubeId;
      }).length;
      console.log('[Playlists] 📊 Filtered tracks:', {
        total: filtered.length,
        withYouTube: withYouTube,
        withoutYouTube: filtered.length - withYouTube,
      });
      
      return filtered;
    }
  }, [
    dataSource,
    allSubmissions, // Use allSubmissions instead of submissions
    rounds,
    users,
    archiveData,
    allRounds,
    leagueName,
    selectedSeasons,
    selectedSubmitters,
    selectedRanks,
    selectedRoundIds,
    searchQuery,
  ]);

  // Convert filtered tracks to PlaylistTrack format
  const playlistTracks = useMemo((): PlaylistTrack[] => {
    const beforeYouTubeFilter = filteredTracks.length;
    const tracksWithYouTube = filteredTracks.filter((track) => track.youtubeId);
    const tracksWithoutYouTube = filteredTracks.filter((track) => !track.youtubeId);
    
    // Process tracks with YouTube IDs
    const tracksToProcess = tracksWithYouTube;
    
    console.log('[Playlists] 🎵 Converting to playlist tracks:', {
      beforeYouTubeFilter,
      withYouTube: tracksWithYouTube.length,
      withoutYouTube: tracksWithoutYouTube.length,
      removed: beforeYouTubeFilter - tracksWithYouTube.length,
      processing: tracksToProcess.length,
    });

    return tracksToProcess
      .map((track) => {
        let reactions: PlaylistReaction[] = [];
        
        // For archive data, parse comments into reactions
        if (dataSource === 'archive' && (track as any).archiveEntry) {
          const archiveEntry = (track as any).archiveEntry as ArchiveEntry;
          // Use the same parsing logic as mapArchiveEntryToTrack
          if (archiveEntry.comments) {
            const segments = archiveEntry.comments.split("|");
            segments.forEach((segment: string, index: number) => {
              const trimmed = segment.trim();
              if (!trimmed) {
                return;
              }
              
              const match = trimmed.match(/^\s*([^:]+):\s*([-+]?\d+(?:\.\d+)?)\s*:\s*(.*)$/);
              if (match) {
                const [, usernameRaw, pointsRaw, commentRaw] = match;
                reactions.push({
                  id: `${archiveEntry.round_id}-${archiveEntry.submitter_id}-comment-${index}`,
                  username: usernameRaw.trim(),
                  points: Number(pointsRaw),
                  comment: commentRaw.trim(),
                });
              } else {
                reactions.push({
                  id: `${archiveEntry.round_id}-${archiveEntry.submitter_id}-comment-${index}`,
                  username: "Judge",
                  points: 0,
                  comment: trimmed,
                });
              }
            });
          }
        }
        
        // Find league from round for live data
        let leagueId: string | undefined = undefined;
        if (dataSource === 'live') {
          const round = rounds.find((r) => r.id === track.roundId);
          if (round) {
            leagueId = round.league_id;
          }
        }
        
        const baseTrack: PlaylistTrack & { score?: number; rank?: number; roundName?: string } = {
          submissionId: track.submissionId,
          roundId: track.roundId,
          leagueId: leagueId,
          title: track.title,
          artist: track.artist,
          youtubeId: track.youtubeId || undefined,
          submitter: track.submitter,
          comment: track.comment,
          thumbnail: track.thumbnail,
          roundNumber: undefined,
          reactions: reactions.length > 0 ? reactions : undefined,
          fun_facts: dataSource === 'archive' && (track as any).archiveEntry 
            ? (track as any).archiveEntry.fun_facts 
            : undefined,
        };
        // Preserve score, rank, round name, and archiveEntry for archive data
        if (dataSource === 'archive') {
          (baseTrack as any).score = track.score;
          (baseTrack as any).rank = track.rank;
          (baseTrack as any).roundName = track.roundName;
          (baseTrack as any).archiveEntry = (track as any).archiveEntry; // Preserve archiveEntry for accessing comments/fun_facts
        }
        return baseTrack;
      });
  }, [filteredTracks, dataSource, rounds]);

  const smartlinkUrl = useMemo(() => {
    if (selectedPlaylistId !== 'none' && selectedPlaylistType === 'curated') {
      const selectedPlaylist = curatedPlaylists.find(p => p.id === selectedPlaylistId);
      return selectedPlaylist?.smartlink_url || null;
    }

    if (selectedSeasons.length === 1 && selectedPlaylistId === 'none') {
      const seasonName = allSeasonsFromDB.find(s => s.id === selectedSeasons[0])?.name;
      const roundsForSeason = allRounds.filter(r => r.season === seasonName);
      const roundWithSmartlink = roundsForSeason.find(r => getSmartlinkUrl(r));
      return roundWithSmartlink ? getSmartlinkUrl(roundWithSmartlink) : null;
    }

    return null;
  }, [
    selectedPlaylistId,
    selectedPlaylistType,
    curatedPlaylists,
    selectedSeasons,
    allSeasonsFromDB,
    allRounds,
  ]);

  // Log when filters change
  useEffect(() => {
    console.log('[Playlists] 🔄 Filters changed:', {
      selectedSeasons,
      selectedSubmitters: selectedSubmitters.length,
      selectedRanks,
      selectedRoundIds,
      searchQuery,
      filteredTracksCount: filteredTracks.length,
      playlistTracksCount: playlistTracks.length,
      dataSource,
    });
  }, [selectedSeasons, selectedSubmitters, selectedRanks, selectedRoundIds, searchQuery, filteredTracks.length, playlistTracks.length, dataSource]);

  // Handle playlist selection
  useEffect(() => {
    if (selectedPlaylistId === 'none' || selectedPlaylistType === 'none') {
      return;
    }

    if (selectedPlaylistType === 'curated') {
      const curated = curatedPlaylists.find((p) => p.id === selectedPlaylistId);
      if (curated) {
        setSelectedSeasons(curated.filters.seasons || []);
        setSelectedSubmitters(curated.filters.submitters || []);
        setSelectedRanks(curated.filters.ranks || []);
        setSelectedRoundIds(curated.filters.roundIds || []);
        if (curated.filters.topThreeOnly) {
          setSelectedRanks([1, 2, 3]);
        }
      }
    } else if (selectedPlaylistType === 'personal') {
      const personal = playlists.find((p) => p.id === selectedPlaylistId);
      if (personal) {
        // Apply personal playlist filters
        if (personal.filters.season) {
          setSelectedSeasons([personal.filters.season]);
        }
        if (personal.filters.submitterIds) {
          setSelectedSubmitters(personal.filters.submitterIds);
        }
        if (personal.filters.topThreeOnly) {
          setSelectedRanks([1, 2, 3]);
        }
        setSelectedRoundIds([]);
      }
    }
  }, [selectedPlaylistId, selectedPlaylistType, curatedPlaylists, playlists]);

  const handlePlay = useCallback(() => {
    if (playlistTracks.length === 0) {
      toast({
        title: 'No tracks to play',
        description: 'No playable tracks match your current filters.',
        variant: 'destructive',
      });
      return;
    }

    // Set playlist in playback context
    const playlistName = selectedPlaylistId !== 'none' 
      ? (selectedPlaylistType === 'curated' 
          ? curatedPlaylists.find(p => p.id === selectedPlaylistId)?.name
          : playlists.find(p => p.id === selectedPlaylistId)?.name)
      : 'Filtered Playlist';

    playback.setPlaylist(playlistTracks, {
      id: `playlist_${selectedPlaylistId !== 'none' ? selectedPlaylistId : Date.now()}`,
      label: playlistName || 'Playlist',
      autoplay: false,
      shuffle: false,
    });

    // Small delay to ensure state updates before navigation
    setTimeout(() => {
      navigate('/playlists/player');
    }, 100);
  }, [playlistTracks, selectedPlaylistId, selectedPlaylistType, curatedPlaylists, playlists, playback, navigate, toast]);

  const handleCreatePlaylist = useCallback(() => {
    if (playlistTracks.length === 0) {
      toast({
        title: 'Cannot create empty playlist',
        description: 'Apply filters to include at least one track.',
        variant: 'destructive',
      });
      return;
    }

    if (!playlistName.trim()) {
      toast({
        title: 'Playlist name required',
        description: 'Please enter a name for your playlist.',
        variant: 'destructive',
      });
      return;
    }

    const filters: PlaylistFilters = {
      season: selectedSeasons.length === 1 ? selectedSeasons[0] : undefined,
      submitterIds: selectedSubmitters.length > 0 ? selectedSubmitters : undefined,
      topThreeOnly: selectedRanks.length === 3 && selectedRanks.every(r => [1, 2, 3].includes(r)),
    };

    createPlaylist({
      name: playlistName.trim(),
      description: playlistDescription.trim() || undefined,
      filters,
      trackIds: playlistTracks.map((t) => t.submissionId),
    });

    toast({
      title: 'Playlist created',
      description: `${playlistName} has been saved.`,
    });

    setShowCreateDialog(false);
    setPlaylistName('');
    setPlaylistDescription('');
  }, [playlistTracks, playlistName, playlistDescription, selectedSeasons, selectedSubmitters, selectedRanks, createPlaylist, toast]);

  const handleResetFilters = useCallback(() => {
    setSelectedSeasons([]);
    setSelectedSubmitters([]);
    setSelectedRanks([]);
    setSelectedRoundIds([]);
    setSearchQuery('');
    setSelectedPlaylistId('none');
    setSelectedPlaylistType('none');
  }, []);

  // Results stay collapsed until the user expands them.

  const toggleSeason = useCallback((season: string) => {
    console.log('[Playlists] 🎯 Toggling season:', season);
    setSelectedSeasons((prev) => {
      const newSeasons = prev.includes(season) 
        ? prev.filter((s) => s !== season) 
        : [...prev, season];
      console.log('[Playlists] 📋 Selected seasons:', newSeasons);
      return newSeasons;
    });
  }, []);

  const toggleSubmitter = useCallback((submitterId: string) => {
    setSelectedSubmitters((prev) =>
      prev.includes(submitterId) ? prev.filter((id) => id !== submitterId) : [...prev, submitterId]
    );
  }, []);

  const toggleRank = useCallback((rank: number) => {
    setSelectedRanks((prev) =>
      prev.includes(rank) ? prev.filter((r) => r !== rank) : [...prev, rank]
    );
  }, []);

  // curatedPlaylists is already fetched from database above
  // Only determine current season for live data, not archive data
  const currentSeason = dataSource === 'live' 
    ? rounds.find((r) => r.status === 'submitting' || r.status === 'voting')?.season || null
    : null;

  // Multi-select dropdown component
  const MultiSelectDropdown = ({ 
    label, 
    options, 
    selected, 
    onToggle, 
    renderOption,
    getValue
  }: { 
    label: string; 
    options: any[]; 
    selected: any[]; 
    onToggle: (value: any) => void;
    renderOption: (option: any) => React.ReactNode;
    getValue: (option: any) => any;
  }) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSelected, setTempSelected] = useState<any[]>(selected);
    const appliedRef = useRef(false);
    
    // Sync tempSelected with selected when dropdown opens/closes
    useEffect(() => {
      if (open) {
        setTempSelected(selected);
        appliedRef.current = false;
      }
    }, [open, selected]);
    
    // Filter options based on search query
    const filteredOptions = useMemo(() => {
      if (!searchQuery.trim()) return options;
      const query = searchQuery.toLowerCase();
      return options.filter((option) => {
        const displayText = String(renderOption(option)).toLowerCase();
        return displayText.includes(query);
      });
    }, [options, searchQuery, renderOption]);
    
    const handleTempToggle = (value: any) => {
      setTempSelected((prev) => {
        if (prev.includes(value)) {
          return prev.filter((v) => v !== value);
        } else {
          return [...prev, value];
        }
      });
    };
    
    const handleApply = () => {
      // Apply temp selections to actual selections
      tempSelected.forEach((value) => {
        if (!selected.includes(value)) {
          onToggle(value);
        }
      });
      // Remove items that were deselected
      selected.forEach((value) => {
        if (!tempSelected.includes(value)) {
          onToggle(value);
        }
      });
      appliedRef.current = true;
      setOpen(false);
    };
    
    const handleClear = () => {
      setTempSelected([]);
    };
    
    return (
      <Popover
        open={open}
        onOpenChange={(next) => {
          // If the popover closes without applying, reset temp selections.
          if (!next && !appliedRef.current) {
            setTempSelected(selected);
          }
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            <span className="truncate">
              {selected.length === 0
                ? `Select ${label}...`
                : selected.length === 1
                  ? `1 ${label} selected`
                  : `${selected.length} ${label} selected`}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0"
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder={`Search ${label}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>
          <div className="max-h-[300px] overflow-y-auto p-1">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No {label} found.
              </div>
            ) : (
              filteredOptions.map((option) => {
                const value = getValue(option);
                const isSelected = tempSelected.includes(value);

                return (
                  <div
                    key={value}
                    className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground"
                    onClick={() => {
                      // Toggle temp selection - dropdown stays open
                      handleTempToggle(value);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 flex-shrink-0",
                        isSelected ? "opacity-100 text-primary" : "opacity-0",
                      )}
                    />
                    <span className={cn("flex-1", isSelected && "font-medium")}>
                      {renderOption(option)}
                    </span>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between border-t px-2 py-2 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="h-8 text-xs"
            >
              Clear
            </Button>
            <div className="text-xs text-muted-foreground">
              {tempSelected.length} selected
            </div>
            <Button
              size="sm"
              onClick={handleApply}
              className="h-8 text-xs"
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-muted/20 to-background" data-tour="playlists">
      <Navigation />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Playlists</h1>
          <p className="text-muted-foreground">
            YouTube Autoplay is limited by mobile browser policies. For the smoothest playback, use a laptop or desktop.
          </p>
        </div>

        {/* Panels */}
        {(() => {
          const selectPlaylistContent = (
            <div className="space-y-4">
              <div>
                <Select
                  value={selectedPlaylistId === 'none' ? 'none' : `${selectedPlaylistType}_${selectedPlaylistId}`}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedPlaylistId('none');
                      setSelectedPlaylistType('none');
                    } else if (value.startsWith('curated_')) {
                      const id = value.replace('curated_', '');
                      setSelectedPlaylistId(id);
                      setSelectedPlaylistType('curated');
                    } else if (value.startsWith('personal_')) {
                      const id = value.replace('personal_', '');
                      setSelectedPlaylistId(id);
                      setSelectedPlaylistType('personal');
                    }
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="-- Choose a Playlist --" />
                  </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- Choose a Playlist --</SelectItem>
                  
                  {curatedPlaylists.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Curated Playlists ({curatedPlaylists.length})
                      </div>
                      {curatedPlaylists
                        .sort((a, b) => {
                          // Sort by sort_index first (highest first), then by name
                          if (a.sort_index !== undefined && b.sort_index !== undefined) {
                            return b.sort_index - a.sort_index; // Higher index first
                          }
                          if (a.sort_index !== undefined) return -1;
                          if (b.sort_index !== undefined) return 1;
                          // Fallback: system-generated playlists first, then by name
                          if (a.name.includes('Top 3') && !b.name.includes('Top 3')) return -1;
                          if (!a.name.includes('Top 3') && b.name.includes('Top 3')) return 1;
                          return a.name.localeCompare(b.name);
                        })
                        .map((playlist) => (
                          <SelectItem key={playlist.id} value={`curated_${playlist.id}`}>
                            {playlist.name}
                            {playlist.description && (
                              <span className="text-xs text-muted-foreground ml-2">
                                - {playlist.description}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                    </>
                  )}
                  
                  {playlists.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        My Playlists ({playlists.length})
                      </div>
                      {playlists.map((playlist) => (
                        <SelectItem key={playlist.id} value={`personal_${playlist.id}`}>
                          {playlist.name}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  
                  {curatedPlaylists.length === 0 && playlists.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground text-center">
                      No playlists available
                    </div>
                  )}
                </SelectContent>
              </Select>
              </div>

              {/* Play selected playlist (shown when a playlist is selected) */}
              {(selectedPlaylistId !== 'none' && selectedPlaylistType !== 'none') && (
                <Button onClick={handlePlay} disabled={playlistTracks.length === 0} className="w-full">
                  <Play className="h-4 w-4 mr-2" />
                  Play Playlist ({playlistTracks.length} tracks)
                </Button>
              )}

              {smartlinkUrl && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-muted-foreground">
                      Smartlinks available for this playlist.
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <a href={smartlinkUrl} target="_blank" rel="noreferrer">
                          Open Smartlinks
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Smartlinks Widget - Show for selected playlist or single selected season */}
              {(() => {
                // Check if selected playlist has a SmartLink URL
                if (selectedPlaylistId !== 'none' && selectedPlaylistType === 'curated') {
                  const selectedPlaylist = curatedPlaylists.find(p => p.id === selectedPlaylistId);
                  if (selectedPlaylist?.smartlink_url) {
                    return (
                      <div className="w-full" id="smartlink-widget">
                        <div className="w-full overflow-hidden rounded-lg border border-border">
                          <iframe
                            frameBorder="0"
                            width="100%"
                            height="300"
                            style={{ maxWidth: '100%' }}
                            src={selectedPlaylist.smartlink_url}
                            title={`Smartlinks for ${selectedPlaylist.name}`}
                          />
                        </div>
                      </div>
                    );
                  }
                }
                
                // Fallback: Show for single selected season if Smartlink URL exists on a round
                if (selectedSeasons.length === 1 && selectedPlaylistId === 'none') {
                  const seasonName = allSeasonsFromDB.find(s => s.id === selectedSeasons[0])?.name;
                  const roundsForSeason = allRounds.filter(r => r.season === seasonName);
                  const roundWithSmartlink = roundsForSeason.find(r => getSmartlinkUrl(r));
                  
                  if (roundWithSmartlink) {
                    return (
                      <div className="w-full" id="smartlink-widget">
                        <div className="w-full overflow-hidden rounded-lg border border-border">
                          <iframe
                            frameBorder="0"
                            width="100%"
                            height="300"
                            style={{ maxWidth: '100%' }}
                            src={getSmartlinkUrl(roundWithSmartlink) || ''}
                            title={`Smartlinks for ${roundWithSmartlink.theme}`}
                          />
                        </div>
                      </div>
                    );
                  }
                }
                
                return null;
              })()}
            
              {/* Display playlist links for selected playlist */}
              {(selectedPlaylistId !== 'none' && selectedPlaylistType !== 'none') && (
                <div className="pt-4 border-t border-primary/20">
                  <PlaylistLinks
                    playlistType={selectedPlaylistType === 'curated' ? 'curated' : 'personal'}
                    playlistId={selectedPlaylistId}
                  />
                </div>
              )}
              
              {/* Display playlist links for single selected season */}
              {selectedSeasons.length === 1 && selectedPlaylistId === 'none' && (
                <div className="pt-4 border-t border-primary/20">
                  <PlaylistLinks
                    playlistType="season"
                    playlistId={selectedSeasons[0]}
                  />
                </div>
              )}
            </div>
          );

          const createPlaylistContent = (
            <div className="space-y-6">
            {/* Search */}
            <div className="space-y-2">
              <Label>Find</Label>
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search all fields..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Filters as Dropdowns */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Seasons</Label>
                <MultiSelectDropdown
                  label="seasons"
                  options={allSeasons}
                  selected={selectedSeasons}
                  onToggle={toggleSeason}
                  getValue={(season) => season}
                  renderOption={(season) => {
                    const isCurrent = currentSeason !== null && season === currentSeason;
                    return (
                      <div className="flex items-center gap-2">
                        <span>{season}</span>
                        {isCurrent && <Badge variant="secondary" className="text-xs">Current</Badge>}
                      </div>
                    );
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Submitters</Label>
                <MultiSelectDropdown
                  label="submitters"
                  options={allSubmitters}
                  selected={selectedSubmitters}
                  onToggle={toggleSubmitter}
                  getValue={(submitter) => submitter.id}
                  renderOption={(submitter) => submitter.name}
                />
              </div>

              <div className="space-y-2">
                <Label>Ranks</Label>
                <MultiSelectDropdown
                  label="ranks"
                  options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
                  selected={selectedRanks}
                  onToggle={toggleRank}
                  getValue={(rank) => rank}
                  renderOption={(rank) => rank.toString()}
                />
              </div>
            </div>

            {/* Action Buttons at Bottom */}
            <div className="flex flex-wrap gap-3 pt-4 border-t">
              <Button onClick={handlePlay} disabled={playlistTracks.length === 0}>
                <Play className="h-4 w-4 mr-2" />
                Play ({playlistTracks.length} tracks)
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(true)}>
                <Save className="h-4 w-4 mr-2" />
                Save Playlist
              </Button>
              <Button variant="outline" onClick={handleResetFilters}>
                Reset
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 dark:bg-primary/20 dark:text-primary dark:border-primary/40">
                  {`${playlistTracks.length} playable track${playlistTracks.length === 1 ? '' : 's'} • ${filteredTracks.length} total displayed`}
                </Badge>
              </div>
            </div>
            </div>
          );

          return (
            <>
              {/* Desktop/tablet: always expanded */}
              <div className="hidden lg:grid grid-cols-2 gap-6 mb-6">
                <Card className="border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
                  <CardHeader className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30">
                    <CardTitle className="flex items-center gap-2 text-primary">
                      <ListMusic className="h-5 w-5" />
                      Select a Playlist
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectPlaylistContent}
                  </CardContent>
                </Card>

                <Card
                  className="border-2 border-blue-300/40 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 dark:from-blue-950/20 dark:to-indigo-950/20 shadow-md"
                  data-tour="create-playlist"
                >
                  <CardHeader className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-blue-300/40">
                    <CardTitle className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                      <Filter className="h-5 w-5" />
                      Create Playlist
                    </CardTitle>
                    <CardDescription>
                      Filter by season, submitter, and rank. Multi-select is supported.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {createPlaylistContent}
                  </CardContent>
                </Card>
              </div>

              {/* Mobile: collapsible, collapsed by default */}
              <div className="lg:hidden mb-6 space-y-4">
                <Card className="border-2 border-primary/30 bg-gradient-to-br from-purple-100/40 to-blue-100/40 dark:from-purple-950/20 dark:to-blue-950/20 shadow-md">
                  <Accordion type="single" collapsible defaultValue="">
                    <AccordionItem value="select" className="border-b-0">
                      <div className="bg-gradient-to-r from-primary/15 to-blue-500/15 dark:from-primary/20 dark:to-blue-400/20 rounded-t-lg border-b border-primary/30 px-6">
                        <AccordionTrigger className="no-underline hover:no-underline py-4">
                          <div className="flex items-center gap-2 text-primary font-semibold">
                            <ListMusic className="h-5 w-5" />
                            Select a Playlist
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent className="pb-0">
                        <CardContent>
                          {selectPlaylistContent}
                        </CardContent>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>

                <Card className="border-2 border-blue-300/40 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 dark:from-blue-950/20 dark:to-indigo-950/20 shadow-md">
                  <Accordion type="single" collapsible defaultValue="">
                    <AccordionItem value="create" className="border-b-0">
                      <div className="bg-gradient-to-r from-blue-500/15 to-indigo-500/15 dark:from-blue-400/20 dark:to-indigo-400/20 rounded-t-lg border-b border-blue-300/40 px-6">
                        <AccordionTrigger className="no-underline hover:no-underline py-4">
                          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold">
                            <Filter className="h-5 w-5" />
                            Create Playlist
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent className="pb-0">
                        <CardContent>
                          {createPlaylistContent}
                        </CardContent>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              </div>
            </>
          );
        })()}

        {/* Results */}
        {(() => {
          const resultsContent = (
            <>
              {filteredTracks.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/8 dark:bg-primary/10 p-8 text-center text-muted-foreground">
                  No tracks match your current filters. Try adjusting your selections.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/70 dark:bg-muted/30">
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Season</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Round</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Title</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Artist</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Submitter</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Score</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-foreground">Rank</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTracks.map((track, index) => (
                        <tr
                          key={track.submissionId}
                          className={cn(
                            "border-b cursor-pointer transition-colors",
                            index % 2 === 0
                              ? "bg-background hover:bg-primary/8 dark:hover:bg-primary/10"
                              : "bg-muted/30 hover:bg-primary/12 dark:hover:bg-primary/15",
                          )}
                          onClick={() => {
                            if (track.youtubeId) {
                              const trackToPlay = playlistTracks.find((t) => t.submissionId === track.submissionId);
                              if (trackToPlay) {
                                playback.setPlaylist(playlistTracks, {
                                  id: `filtered_${Date.now()}`,
                                  label: 'Filtered Results',
                                  autoplay: false,
                                  shuffle: false,
                                });
                                playback.playTrack(track.submissionId);
                              }
                            }
                          }}
                        >
                          <td className="px-4 py-3 text-sm">{track.season}</td>
                          <td className="px-4 py-3 text-sm">{track.roundName}</td>
                          <td className="px-4 py-3 text-sm font-medium">{track.title}</td>
                          <td className="px-4 py-3 text-sm">{track.artist}</td>
                          <td className="px-4 py-3 text-sm">{track.submitter}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-primary">{Math.round(track.score)}</td>
                          <td className="px-4 py-3 text-sm">
                            {track.rank} out of {track.totalInRound}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );

          return (
            <>
              <div className="hidden lg:block">
                <Card className="border-2 border-indigo-300/40 bg-gradient-to-br from-indigo-100/30 to-purple-100/30 dark:from-indigo-950/15 dark:to-purple-950/15 shadow-md">
                  <Accordion type="single" collapsible value={mobileResultsOpen} onValueChange={setMobileResultsOpen}>
                    <AccordionItem value="results" className="border-b-0">
                      <div className="bg-gradient-to-r from-accent/15 via-primary/15 to-indigo-500/15 dark:from-accent/20 dark:via-primary/20 dark:to-indigo-400/20 rounded-t-lg border-b border-indigo-300/40 px-6">
                        <AccordionTrigger className="no-underline hover:no-underline py-4">
                          <div className="flex flex-col items-start">
                            <div className="text-indigo-600 dark:text-indigo-400 font-semibold">Results</div>
                            <div className="text-xs text-muted-foreground">
                              {filteredTracks.length} track{filteredTracks.length === 1 ? '' : 's'} matching your filters
                            </div>
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent className="pb-0">
                        <CardContent>{resultsContent}</CardContent>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              </div>

              <div className="lg:hidden">
                <Card className="border-2 border-indigo-300/40 bg-gradient-to-br from-indigo-100/30 to-purple-100/30 dark:from-indigo-950/15 dark:to-purple-950/15 shadow-md">
                  <Accordion type="single" collapsible value={mobileResultsOpen} onValueChange={setMobileResultsOpen}>
                    <AccordionItem value="results" className="border-b-0">
                      <div className="bg-gradient-to-r from-accent/15 via-primary/15 to-indigo-500/15 dark:from-accent/20 dark:via-primary/20 dark:to-indigo-400/20 rounded-t-lg border-b border-indigo-300/40 px-6">
                        <AccordionTrigger className="no-underline hover:no-underline py-4">
                          <div className="flex flex-col items-start">
                            <div className="text-indigo-600 dark:text-indigo-400 font-semibold">Results</div>
                            <div className="text-xs text-muted-foreground">
                              {filteredTracks.length} track{filteredTracks.length === 1 ? '' : 's'} matching your filters
                            </div>
                          </div>
                        </AccordionTrigger>
                      </div>
                      <AccordionContent className="pb-0">
                        <CardContent>{resultsContent}</CardContent>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              </div>
            </>
          );
        })()}

        {/* Create Playlist Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Playlist</DialogTitle>
              <DialogDescription>
                Save your current filter selections as a personal playlist.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="playlistName">Playlist Name *</Label>
                <Input
                  id="playlistName"
                  value={playlistName}
                  onChange={(e) => setPlaylistName(e.target.value)}
                  placeholder="My Custom Playlist"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="playlistDescription">Description (optional)</Label>
                <Input
                  id="playlistDescription"
                  value={playlistDescription}
                  onChange={(e) => setPlaylistDescription(e.target.value)}
                  placeholder="A description of this playlist"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreatePlaylist} disabled={!playlistName.trim()}>
                <Save className="h-4 w-4 mr-2" />
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default Playlists;
